import styled from "@emotion/styled";
import React, { useRef, useState } from "react";
import * as FileSaver from "file-saver";

import useOnClickOutside from "@src/hooks/useOnClickOutside";
import { ReactComponent as DownloadIcon } from "@assets/icons/download.svg";

export type TTrackExportFormat = "csv" | "kml" | "shp";

/** Every track export offers the same three formats, in this order. */
export const TRACK_EXPORT_FORMATS: ReadonlyArray<{
  format: TTrackExportFormat;
  name: string;
  ext: string;
}> = [
  { format: "csv", name: "CSV", ext: "csv" },
  { format: "kml", name: "KML", ext: "kml" },
  { format: "shp", name: "Shapefile", ext: "shp.zip" },
];

export const trackExportExt = (format: TTrackExportFormat): string =>
  TRACK_EXPORT_FORMATS.find((f) => f.format === format)!.ext;

/**
 * Fetch an export and hand it to the browser as `filename`.
 *
 * fetch rather than window.open so a refusal (413 too many points, 404 nothing
 * to export) surfaces as the server's message instead of a JSON tab.
 */
export async function downloadTrackExport(url: string, filename: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? body?.message ?? `Unexpected response status: ${response.status}`);
  }
  FileSaver.saveAs(await response.blob(), filename);
}

const Anchor = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

/** Floating over the map: the Tracks screen's action-button look. */
const OverlayTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #494c5b;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
  &:disabled {
    cursor: default;
    color: #b4b7c0;
  }
  svg {
    width: 14px;
    height: 14px;
  }
  svg path {
    fill: #68ad18;
  }
`;

/** Inside a side panel: a full-width small button. */
const PanelTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  padding: 5px 6px;
  border: 1px solid #e0e2e7;
  border-radius: 4px;
  background: #fff;
  color: #494c5b;
  cursor: pointer;
  &:disabled {
    cursor: default;
    color: #b4b7c0;
  }
  svg {
    width: 12px;
    height: 12px;
  }
  svg path {
    fill: #68ad18;
  }
`;

const Chevron = styled("svg", {
  shouldForwardProp: (p) => p !== "open",
})<{ open: boolean }>`
  transition: transform 0.15s ease;
  transform: rotate(${({ open }) => (open ? "180deg" : "0deg")});
  && path {
    fill: none;
  }
`;

/**
 * Over the map it floats under the button. In a side panel it opens in flow:
 * the panel scrolls (overflow: auto), so a floating menu would be clipped.
 */
const Menu = styled("div", {
  shouldForwardProp: (p) => p !== "floating",
})<{ floating: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  padding: 4px;
  background: #fff;
  border: 1px solid #e0e2e7;
  border-radius: 6px;
  ${({ floating }) =>
    floating
      ? "position: absolute; top: calc(100% + 6px); right: 0; z-index: 30; max-height: 40vh; overflow-y: auto; box-shadow: 0 2px 10px rgba(78, 78, 81, 0.15);"
      : "margin-top: 4px;"}
`;

const GroupTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #8c8f9b;
  padding: 4px 8px 2px;
  white-space: nowrap;
`;

const FormatRow = styled.div`
  display: flex;
  gap: 4px;
  padding: 2px 4px 4px;
`;

const FormatItem = styled.button`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  background: none;
  color: #494c5b;
  cursor: pointer;
  &:hover,
  &:focus-visible {
    background: #f0f2f3;
    outline: none;
  }
`;

const FormatChip = styled(FormatItem)`
  flex: 1;
  text-align: center;
  font-size: 12px;
  padding: 4px 8px;
  border: 1px solid #e0e2e7;
`;

export type TrackExportMenuProps = {
  label: string;
  variant: "overlay" | "panel";
  disabled?: boolean;
  /** Several things to export (machines on the Tracks screen): each gets its
   *  own row of formats. One or none: the menu is just the formats. */
  targets?: ReadonlyArray<{ id: string; label: string }>;
  onExport: (format: TTrackExportFormat, targetId?: string) => void;
};

/**
 * "Export ▾" — one dropdown for every track export, so the Tracks screen and
 * Fields → tracks offer the same formats the same way: click, pick a format,
 * the file downloads.
 */
const TrackExportMenu: React.FC<TrackExportMenuProps> = (props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setOpen(false));

  const Trigger = props.variant === "overlay" ? OverlayTrigger : PanelTrigger;
  const pick = (format: TTrackExportFormat, targetId?: string) => {
    setOpen(false);
    props.onExport(format, targetId);
  };
  const grouped = (props.targets?.length ?? 0) > 1;

  return (
    <Anchor
      ref={ref}
      style={props.variant === "overlay" ? { alignItems: "flex-end" } : undefined}
      onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
    >
      <Trigger
        type="button"
        disabled={props.disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <DownloadIcon />
        {props.label}
        <Chevron
          open={open}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          stroke="#494c5b"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </Chevron>
      </Trigger>
      {open && (
        <Menu role="menu" floating={props.variant === "overlay"}>
          {grouped
            ? props.targets!.map((t) => (
                <div key={t.id}>
                  <GroupTitle>{t.label}</GroupTitle>
                  <FormatRow>
                    {TRACK_EXPORT_FORMATS.map(({ format, name }) => (
                      <FormatChip
                        key={format}
                        type="button"
                        role="menuitem"
                        onClick={() => pick(format, t.id)}
                      >
                        {name}
                      </FormatChip>
                    ))}
                  </FormatRow>
                </div>
              ))
            : TRACK_EXPORT_FORMATS.map(({ format, name }) => (
                <FormatItem
                  key={format}
                  type="button"
                  role="menuitem"
                  onClick={() => pick(format, props.targets?.[0]?.id)}
                >
                  {name}
                </FormatItem>
              ))}
        </Menu>
      )}
    </Anchor>
  );
};

export default TrackExportMenu;
